/**
 * Client-safe presenter map for every inbound provider Brew ships.
 *
 * Importing an adapter's `index.ts` registers it on the registry but
 * also pulls in server-only Convex helpers — fine on routes /
 * server actions, NOT fine inside a client component bundle. This
 * module imports just the presenter (`presenter.ts`) modules, which
 * are pure data declarations.
 *
 * Use `getInboundPresenter(providerId)` from any client surface that
 * needs to render presenter-driven UI (the integration page event
 * panel, the Settings tab wrapper).
 */
import { clerkInboundPresenter } from './providers/clerk/presenter'
import { framerInboundPresenter } from './providers/framer/presenter'
import { revenuecatInboundPresenter } from './providers/revenuecat/presenter'
import { shopifyInboundPresenter } from './providers/shopify/presenter'
import { stytchInboundPresenter } from './providers/stytch/presenter'
import { supabaseInboundPresenter } from './providers/supabase/presenter'
import { workosInboundPresenter } from './providers/workos/presenter'
import type { InboundProviderPresenter } from './types'

const INBOUND_PROVIDER_PRESENTERS = {
  framer: framerInboundPresenter,
  clerk: clerkInboundPresenter,
  stytch: stytchInboundPresenter,
  supabase: supabaseInboundPresenter,
  workos: workosInboundPresenter,
  shopify: shopifyInboundPresenter,
  revenuecat: revenuecatInboundPresenter,
} as const satisfies Record<string, InboundProviderPresenter>

export type RegisteredInboundProviderId =
  keyof typeof INBOUND_PROVIDER_PRESENTERS

export function getInboundPresenter(
  providerId: string
): InboundProviderPresenter | null {
  return (
    INBOUND_PROVIDER_PRESENTERS[providerId as RegisteredInboundProviderId] ??
    null
  )
}

export function isInboundProvider(
  providerId: string
): providerId is RegisteredInboundProviderId {
  return providerId in INBOUND_PROVIDER_PRESENTERS
}
